import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronLeft,
  Copy,
  Edit3,
  FileText,
  Home,
  Info,
  Loader2,
  Mail,
  MessageCircle,
  MoreHorizontal,
  Search,
  Send,
  Share2,
  Trash2,
  Users,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSupabase } from '../../contexts/SupabaseContext';
import type { EmailCampaign } from '../../pages/marketing/Campaigns';

type DetailsTab = 'overview' | 'content' | 'audience';

interface RecipientRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  status: string | null;
  first_opened_at: string | null;
  first_clicked_at: string | null;
}

type AudienceFilter =
  | 'all'
  | 'delivered'
  | 'opened'
  | 'clicked'
  | 'bounced'
  | 'unsubscribed'
  | 'complained';

const RECIPIENT_PAGE_SIZE = 200;

const AUDIENCE_FILTERS: { id: AudienceFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'opened', label: 'Opened' },
  { id: 'clicked', label: 'Clicked' },
  { id: 'bounced', label: 'Bounced' },
  { id: 'unsubscribed', label: 'Unsubs' },
  { id: 'complained', label: 'Spam' },
];

const RECIPIENT_BADGE: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'bg-gray-100 text-gray-600' },
  sent: { label: 'Sent', cls: 'bg-blue-50 text-blue-700' },
  delivered: { label: 'Delivered', cls: 'bg-green-50 text-green-700' },
  opened: { label: 'Opened', cls: 'bg-purple-50 text-purple-700' },
  clicked: { label: 'Clicked', cls: 'bg-indigo-50 text-indigo-700' },
  bounced: { label: 'Bounced', cls: 'bg-red-50 text-red-700' },
  failed: { label: 'Failed', cls: 'bg-red-50 text-red-700' },
  unsubscribed: { label: 'Unsubscribed', cls: 'bg-yellow-50 text-yellow-700' },
  complained: { label: 'Spam report', cls: 'bg-red-50 text-red-700' },
};

/** 98 → "98%", 6.55 → "6.6%" — whole numbers above 10, one decimal below. */
function fmtPct(num: number, den: number): string {
  if (den <= 0) return '0%';
  const v = (num / den) * 100;
  return v >= 10 || v === 0 ? `${Math.round(v)}%` : `${(Math.round(v * 10) / 10).toFixed(1)}%`;
}

function fmtStamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function SectionHeader({ label, info }: { label: string; info?: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">
        {label}
      </span>
      {info ? (
        <button
          type="button"
          onClick={() => toast(info, { duration: 5000 })}
          className="text-gray-400 active:text-gray-600 touch-manipulation"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          aria-label={`About ${label}`}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      ) : null}
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
}

export default function CampaignDetailsSheet({
  campaign,
  onClose,
  onEdit,
  onLaunch,
  onDuplicate,
  onDelete,
  onRetryBilling,
}: {
  campaign: EmailCampaign;
  onClose: () => void;
  onEdit: (c: EmailCampaign) => void;
  onLaunch: (c: EmailCampaign) => void;
  onDuplicate: (c: EmailCampaign) => void;
  onDelete: (id: string) => void;
  onRetryBilling?: (c: EmailCampaign) => void;
}) {
  const supabase = useSupabase();

  const [tab, setTab] = useState<DetailsTab>('overview');
  const [moreOpen, setMoreOpen] = useState(false);
  const [spamCount, setSpamCount] = useState<number | null>(null);
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [recipientsLoaded, setRecipientsLoaded] = useState(false);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [hasMoreRecipients, setHasMoreRecipients] = useState(false);
  const [audienceSearch, setAudienceSearch] = useState('');
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilter>('all');

  const isEmail = campaign.channel_type !== 'sms';
  const isSent = campaign.status === 'sent';
  const isSending = campaign.status === 'sending';
  const isScheduled = campaign.status === 'scheduled';
  const isDraft = campaign.status === 'draft';
  const isFailed = campaign.status === 'failed';
  const billingFailed = campaign.billing_status === 'failed';
  const alreadyPaid =
    campaign.billing_status === 'captured' && !!campaign.billing_payment_intent_id;
  const needsBillingRetry = isEmail && (billingFailed || isFailed);
  const canEdit = isDraft || isScheduled || campaign.status === 'paused' || isFailed;
  const hasStats = isSent || isSending;

  const sent = campaign.sent_count || 0;
  const delivered = campaign.delivered_count || 0;
  const bounced = campaign.bounced_count || 0;
  const opened = campaign.opened_count || 0;
  const clicked = campaign.clicked_count || 0;
  const unsubs = campaign.unsubscribed_count || 0;

  const statusPill: Record<string, { label: string; cls: string }> = {
    draft: { label: 'Draft', cls: 'border-gray-300 text-gray-700 bg-gray-50' },
    scheduled: { label: 'Scheduled', cls: 'border-purple-300 text-purple-700 bg-purple-50' },
    sending: { label: 'Sending', cls: 'border-blue-300 text-blue-700 bg-blue-50' },
    sent: { label: 'Delivered/Completed', cls: 'border-green-300 text-green-700 bg-green-50' },
    paused: { label: 'Paused', cls: 'border-yellow-300 text-yellow-700 bg-yellow-50' },
    failed: { label: 'Failed', cls: 'border-red-300 text-red-700 bg-red-50' },
  };
  const pill = statusPill[campaign.status] || statusPill.draft;

  const timingChip = campaign.sent_at
    ? `Sent ${fmtStamp(campaign.sent_at)}`
    : campaign.scheduled_at
      ? `Scheduled ${fmtStamp(campaign.scheduled_at)}`
      : 'Draft';

  // Spam reports aren't aggregated on the campaign row — count complained recipients.
  useEffect(() => {
    if (!isEmail || !hasStats) return;
    let cancelled = false;
    void (async () => {
      const { count } = await supabase
        .from('email_campaign_recipients')
        .select('id', { count: 'exact', head: true })
        .eq('campaign_id', campaign.id)
        .eq('status', 'complained');
      if (!cancelled) setSpamCount(count ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [supabase, campaign.id, isEmail, hasStats]);

  const loadRecipients = useCallback(
    async (offset: number) => {
      setRecipientsLoading(true);
      try {
        const { data, error } = await supabase
          .from('email_campaign_recipients')
          .select('id, first_name, last_name, email, phone, status, first_opened_at, first_clicked_at')
          .eq('campaign_id', campaign.id)
          .order('created_at', { ascending: true })
          .range(offset, offset + RECIPIENT_PAGE_SIZE - 1);
        if (error) throw error;
        const rows = (data || []) as RecipientRow[];
        setRecipients((prev) => (offset === 0 ? rows : [...prev, ...rows]));
        setHasMoreRecipients(rows.length === RECIPIENT_PAGE_SIZE);
      } catch (err) {
        console.error('Failed to load campaign audience:', err);
        toast.error('Failed to load audience');
      } finally {
        setRecipientsLoading(false);
        setRecipientsLoaded(true);
      }
    },
    [supabase, campaign.id],
  );

  useEffect(() => {
    if (tab === 'audience' && !recipientsLoaded && !recipientsLoading) {
      void loadRecipients(0);
    }
  }, [tab, recipientsLoaded, recipientsLoading, loadRecipients]);

  const filteredRecipients = useMemo(() => {
    const q = audienceSearch.trim().toLowerCase();
    return recipients.filter((r) => {
      if (audienceFilter !== 'all') {
        const s = r.status || 'pending';
        const matches =
          audienceFilter === 'delivered'
            ? ['delivered', 'opened', 'clicked'].includes(s)
            : audienceFilter === 'opened'
              ? !!r.first_opened_at || ['opened', 'clicked'].includes(s)
              : audienceFilter === 'clicked'
                ? !!r.first_clicked_at || s === 'clicked'
                : audienceFilter === 'bounced'
                  ? ['bounced', 'failed'].includes(s)
                  : s === audienceFilter;
        if (!matches) return false;
      }
      if (!q) return true;
      const name = `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase();
      return (
        name.includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.phone || '').toLowerCase().includes(q)
      );
    });
  }, [recipients, audienceFilter, audienceSearch]);

  const handleShare = async () => {
    const lines = [
      `${campaign.name} — ${isEmail ? 'Email' : 'SMS'} campaign`,
      campaign.sent_at ? `Sent ${fmtStamp(campaign.sent_at)}` : `Status: ${pill.label}`,
      `Audience: ${(campaign.recipient_count || 0).toLocaleString()}`,
    ];
    if (hasStats) {
      lines.push(`Sent: ${sent.toLocaleString()}`);
      lines.push(`Delivered: ${delivered.toLocaleString()} (${fmtPct(delivered, sent)})`);
      if (isEmail) {
        lines.push(`Opens: ${opened.toLocaleString()} (${fmtPct(opened, delivered)})`);
        lines.push(`Clicks: ${clicked.toLocaleString()} (${fmtPct(clicked, delivered)})`);
      }
    }
    const text = lines.join('\n');
    try {
      if (navigator.share) {
        await navigator.share({ title: campaign.name, text });
        return;
      }
    } catch {
      return; // user cancelled the share sheet
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Summary copied to clipboard');
    } catch {
      toast.error('Could not copy summary');
    }
  };

  const closeAnd = (fn: () => void) => {
    setMoreOpen(false);
    onClose();
    fn();
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const barItems: { id: DetailsTab | 'more'; label: string; icon: typeof Home }[] = [
    { id: 'overview', label: 'Overview', icon: Home },
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'audience', label: 'Audience', icon: Users },
    { id: 'more', label: 'More', icon: MoreHorizontal },
  ];

  return createPortal(
    <div
      className="fixed inset-0 transition-opacity duration-200 bg-black/50"
      style={{ zIndex: 100010, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-transform duration-200 ${
          isMobile ? 'fixed inset-0 w-full' : 'fixed inset-y-0 right-0 w-full md:w-[560px] lg:w-[620px]'
        }`}
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(!isMobile && {
            height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: Back · Campaign · Share */}
        <div className="shrink-0 bg-white border-b border-gray-200 px-2 sm:px-3 py-2 flex items-center justify-between">
          <button
            type="button"
            onClick={() => (tab !== 'overview' ? setTab('overview') : onClose())}
            className="flex items-center gap-0.5 px-2 py-2 text-gray-900 active:opacity-60 touch-manipulation"
            style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="text-[15px] font-medium">Back</span>
          </button>
          <h2
            className="text-[16px] font-bold text-gray-900 absolute left-1/2 -translate-x-1/2"
            style={{ letterSpacing: '-0.02em' }}
          >
            Campaign
          </h2>
          <button
            type="button"
            onClick={() => void handleShare()}
            className="flex items-center gap-1.5 px-2 py-2 text-gray-900 active:opacity-60 touch-manipulation"
            style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
          >
            <Share2 className="h-[18px] w-[18px]" />
            <span className="text-[15px] font-medium">Share</span>
          </button>
        </div>

        {/* Scrollable content */}
        <div
          className="flex-1 min-h-0 overflow-y-auto bg-white relative"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {tab === 'overview' && (
            <div className="p-4 sm:p-6 space-y-5">
              {/* Hero card */}
              <div className="rounded-[16px] border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <h3
                  className="text-[22px] font-bold text-gray-900 leading-tight"
                  style={{ letterSpacing: '-0.02em' }}
                >
                  {campaign.name}
                </h3>
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-2.5 py-1 text-[12px] font-medium text-gray-700">
                    {isEmail ? (
                      <Mail className="h-3.5 w-3.5 text-gray-500" />
                    ) : (
                      <MessageCircle className="h-3.5 w-3.5 text-gray-500" />
                    )}
                    {isEmail ? 'Email' : 'SMS'} · {timingChip}
                  </span>
                  {campaign.is_ab_test && (
                    <span className="inline-flex items-center rounded-full border border-purple-300 bg-purple-50 px-2.5 py-1 text-[12px] font-medium text-purple-700">
                      A/B Test
                    </span>
                  )}
                </div>
                <div className="mt-4 flex items-center justify-between gap-3">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="text-[13px] text-gray-500">Audience</span>
                    <span className="text-[15px] font-bold text-gray-900">
                      {(campaign.recipient_count || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[13px] text-gray-500">Status</span>
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap ${pill.cls}`}
                    >
                      {pill.label}
                    </span>
                  </div>
                </div>
              </div>

              {hasStats ? (
                <>
                  {/* Delivery */}
                  <section>
                    <SectionHeader
                      label="Delivery"
                      info="Sent is how many messages were handed off to the provider. Delivered is how many reached inboxes. Bounced could not be delivered."
                    />
                    <div className="rounded-[14px] border border-gray-200 bg-white flex divide-x divide-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                      <div className="flex-1 px-3 py-3.5 min-w-0">
                        <p className="text-[12px] text-gray-500 font-medium">Sent</p>
                        <p className="text-[20px] font-bold text-gray-900 leading-tight mt-0.5">
                          {sent.toLocaleString()}
                        </p>
                      </div>
                      <div className="flex-1 px-3 py-3.5 min-w-0">
                        <p className="text-[12px] text-gray-500 font-medium">Delivered</p>
                        <p className="text-[20px] font-bold text-gray-900 leading-tight mt-0.5">
                          {delivered.toLocaleString()}
                          <span className="text-[13px] font-semibold text-gray-500 ml-1">
                            ({fmtPct(delivered, sent)})
                          </span>
                        </p>
                      </div>
                      <div className="flex-1 px-3 py-3.5 min-w-0">
                        <p className="text-[12px] text-gray-500 font-medium">Bounced</p>
                        <p className="text-[20px] font-bold text-gray-900 leading-tight mt-0.5">
                          {bounced.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Engagement (email only) */}
                  {isEmail && (
                    <section>
                      <SectionHeader
                        label="Engagement (email)"
                        info="Unique opens and clicks — each recipient counts once. Rates use delivered as the denominator. CTOR is clicks divided by opens."
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-[14px] border border-gray-200 bg-white p-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                          <p className="text-[13px] text-gray-600 font-medium">Opens</p>
                          <p className="text-[22px] font-bold text-gray-900 leading-tight mt-0.5">
                            {opened.toLocaleString()}
                          </p>
                          <p className="text-[12px] text-gray-500 mt-0.5">
                            unique · {fmtPct(opened, delivered)}
                          </p>
                        </div>
                        <div className="rounded-[14px] border border-gray-200 bg-white p-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                          <p className="text-[13px] text-gray-600 font-medium">Clicks</p>
                          <p className="text-[22px] font-bold text-gray-900 leading-tight mt-0.5">
                            {clicked.toLocaleString()}
                          </p>
                          <p className="text-[12px] text-gray-500 mt-0.5">
                            unique · {fmtPct(clicked, delivered)}
                          </p>
                        </div>
                        <div className="rounded-[14px] border border-gray-200 bg-white p-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                          <div className="flex items-center gap-1">
                            <p className="text-[13px] text-gray-600 font-medium">CTOR</p>
                            <button
                              type="button"
                              onClick={() =>
                                toast('Click-to-open rate: of the people who opened, how many clicked.', {
                                  duration: 5000,
                                })
                              }
                              className="text-gray-400 touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                              aria-label="About CTOR"
                            >
                              <Info className="h-3 w-3" />
                            </button>
                          </div>
                          <p className="text-[22px] font-bold text-gray-900 leading-tight mt-0.5">
                            {fmtPct(clicked, opened)}
                          </p>
                          <p className="text-[12px] text-gray-500 mt-0.5">(clicks÷opens)</p>
                        </div>
                        <div className="rounded-[14px] border border-gray-200 bg-white p-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                          <p className="text-[13px] text-gray-600 font-medium">Unsubs</p>
                          <p className="text-[22px] font-bold text-gray-900 leading-tight mt-0.5">
                            {unsubs.toLocaleString()}
                          </p>
                          <p className="text-[12px] text-gray-500 mt-0.5">
                            · {fmtPct(unsubs, delivered)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 inline-flex items-center gap-2 rounded-[10px] border border-gray-200 px-3 py-1.5">
                        <span className="text-[13px] text-gray-600 font-medium">Spam reports</span>
                        <span className="text-[13px] font-bold text-gray-900">
                          {spamCount === null ? '—' : spamCount.toLocaleString()}
                        </span>
                      </div>

                      <p className="mt-3 text-[11px] text-gray-400">
                        Rates use delivered as denominator; unique opens/clicks.
                      </p>
                    </section>
                  )}
                </>
              ) : (
                /* Not sent yet — show timing instead of stats */
                <section>
                  <SectionHeader label="Timing" />
                  <div className="rounded-[14px] border border-gray-200 bg-white p-4 grid grid-cols-2 gap-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                    <div>
                      <p className="text-[12px] text-gray-500">Created</p>
                      <p className="text-[13px] text-gray-900 font-medium mt-0.5">
                        {new Date(campaign.created_at).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    {campaign.scheduled_at && (
                      <div>
                        <p className="text-[12px] text-gray-500">Scheduled for</p>
                        <p className="text-[13px] text-gray-900 font-medium mt-0.5">
                          {fmtStamp(campaign.scheduled_at)}
                        </p>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Failure / billing banners */}
              {isFailed && (
                <div className="bg-red-50 border border-red-200 rounded-[12px] p-4">
                  <div className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[13px] font-semibold text-red-800">
                        {billingFailed || !alreadyPaid
                          ? 'Campaign payment failed'
                          : 'Campaign failed to send'}
                      </p>
                      <p className="text-[12px] text-red-600 mt-0.5">
                        {billingFailed || !alreadyPaid
                          ? 'Payment could not be completed. Retry the charge from the More menu to send this campaign.'
                          : 'Payment succeeded but delivery failed. You can retry sending from the More menu without being charged again.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {!isFailed && billingFailed && (
                <div className="bg-red-50 border border-red-200 rounded-[12px] p-4">
                  <div className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[13px] font-semibold text-red-800">Campaign payment failed</p>
                      <p className="text-[12px] text-red-600 mt-0.5">
                        Payment could not be completed. Retry the charge from the More menu.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'content' && (
            <div className="p-4 sm:p-6 space-y-4">
              <SectionHeader label={isEmail ? 'Email content' : 'SMS content'} />
              <div className="border border-gray-200/60 rounded-[12px] overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                {isEmail ? (
                  <div className="p-4 space-y-2">
                    {campaign.subject && (
                      <div>
                        <span className="text-[11px] text-gray-500 font-medium">Subject</span>
                        <p className="text-[13px] text-gray-900 font-medium">{campaign.subject}</p>
                      </div>
                    )}
                    {campaign.from_email && (
                      <div>
                        <span className="text-[11px] text-gray-500 font-medium">From</span>
                        <p className="text-[13px] text-gray-900">
                          {campaign.from_name
                            ? `${campaign.from_name} <${campaign.from_email}>`
                            : campaign.from_email}
                        </p>
                      </div>
                    )}
                    {campaign.html_content && (
                      <div className="mt-3 border-t border-gray-100 pt-3">
                        <span className="text-[11px] text-gray-500 font-medium">Body</span>
                        <div
                          className="mt-1 bg-gray-50 rounded-[10px] p-3 text-[13px] text-gray-700 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: campaign.html_content }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-4 space-y-2">
                    {campaign.from_phone_number && (
                      <div>
                        <span className="text-[11px] text-gray-500 font-medium">From</span>
                        <p className="text-[13px] text-gray-900">{campaign.from_phone_number}</p>
                      </div>
                    )}
                    {campaign.sms_content && (
                      <div
                        className={campaign.from_phone_number ? 'mt-2 border-t border-gray-100 pt-2' : ''}
                      >
                        <span className="text-[11px] text-gray-500 font-medium">Message</span>
                        <div className="mt-1 bg-gray-50 rounded-[10px] p-3 text-[13px] text-gray-700 whitespace-pre-wrap">
                          {campaign.sms_content}
                        </div>
                        <span className="text-[11px] text-gray-400 mt-1 block">
                          {campaign.sms_content.length} characters
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'audience' && (
            <div className="p-4 sm:p-6 space-y-3">
              <SectionHeader label={`Audience · ${(campaign.recipient_count || 0).toLocaleString()}`} />
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={audienceSearch}
                  onChange={(e) => setAudienceSearch(e.target.value)}
                  placeholder={isEmail ? 'Search name or email' : 'Search name or phone'}
                  className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                {AUDIENCE_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setAudienceFilter(f.id)}
                    className={`shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold touch-manipulation ${
                      audienceFilter === f.id
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {recipientsLoading && recipients.length === 0 ? (
                <div className="flex justify-center py-12 text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : filteredRecipients.length === 0 ? (
                <div className="border border-dashed border-gray-200 rounded-[14px] px-4 py-10 text-center">
                  <Users className="h-6 w-6 text-gray-300 mx-auto mb-2" />
                  <p className="text-[13px] text-gray-500">
                    {recipients.length === 0
                      ? isDraft || isScheduled
                        ? 'Recipients are locked in when the campaign sends.'
                        : 'No recipients found.'
                      : 'No recipients match.'}
                  </p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-[14px] divide-y divide-gray-100 overflow-hidden bg-white">
                  {filteredRecipients.map((r) => {
                    const name = `${r.first_name || ''} ${r.last_name || ''}`.trim();
                    const contact = isEmail ? r.email : r.phone || r.email;
                    const badge = RECIPIENT_BADGE[r.status || 'pending'] || RECIPIENT_BADGE.pending;
                    const initials =
                      (name || contact || '?')
                        .split(/\s+/)
                        .map((p) => p[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase() || '?';
                    return (
                      <div key={r.id} className="flex items-center gap-3 px-3 py-2.5">
                        <div className="h-9 w-9 shrink-0 rounded-full bg-gray-100 flex items-center justify-center text-[12px] font-bold text-gray-600">
                          {initials}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold text-gray-900 truncate">
                            {name || contact}
                          </p>
                          {name && contact ? (
                            <p className="text-[12px] text-gray-500 truncate">{contact}</p>
                          ) : null}
                        </div>
                        <span
                          className={`shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold ${badge.cls}`}
                        >
                          {badge.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {hasMoreRecipients && !recipientsLoading && (
                <button
                  type="button"
                  onClick={() => void loadRecipients(recipients.length)}
                  className="w-full py-2.5 rounded-[10px] border border-gray-200 text-[13px] font-semibold text-gray-700 active:bg-gray-50 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Load more
                </button>
              )}
              {recipientsLoading && recipients.length > 0 && (
                <div className="flex justify-center py-2 text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pinned bottom bar */}
        <div className="shrink-0 border-t border-gray-200 bg-white flex items-stretch">
          {barItems.map((item) => {
            const Icon = item.icon;
            const active = item.id === 'more' ? moreOpen : tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  if (item.id === 'more') setMoreOpen(true);
                  else setTab(item.id);
                }}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 touch-manipulation"
                style={{ minHeight: 52, WebkitTapHighlightColor: 'transparent' }}
              >
                <Icon
                  className={`h-5 w-5 ${active ? 'text-gray-900' : 'text-gray-400'}`}
                  strokeWidth={active ? 2.2 : 2}
                />
                <span
                  className={`text-[10px] font-semibold ${active ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* More actions sheet */}
        {moreOpen && (
          <div className="absolute inset-0 z-20" onClick={() => setMoreOpen(false)}>
            <div className="absolute inset-0 bg-black/30" />
            <div
              className="absolute bottom-0 inset-x-0 bg-white rounded-t-[16px] shadow-2xl p-2 pb-3"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto mb-2 mt-1 h-1 w-10 rounded-full bg-gray-300" />
              {needsBillingRetry && onRetryBilling && (
                <button
                  type="button"
                  onClick={() => closeAnd(() => onRetryBilling(campaign))}
                  className="flex w-full items-center gap-3 rounded-[10px] px-4 py-3 text-left text-[14px] font-semibold text-red-600 active:bg-red-50 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Send className="h-4 w-4" />
                  {alreadyPaid ? 'Retry send' : 'Retry charge & send'}
                </button>
              )}
              {(isDraft || isScheduled) && !needsBillingRetry && (
                <button
                  type="button"
                  onClick={() => closeAnd(() => onLaunch(campaign))}
                  className="flex w-full items-center gap-3 rounded-[10px] px-4 py-3 text-left text-[14px] font-semibold text-blue-600 active:bg-blue-50 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Send className="h-4 w-4" />
                  {isScheduled ? 'Send now' : 'Launch'}
                </button>
              )}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => closeAnd(() => onEdit(campaign))}
                  className="flex w-full items-center gap-3 rounded-[10px] px-4 py-3 text-left text-[14px] font-medium text-gray-800 active:bg-gray-50 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Edit3 className="h-4 w-4 text-gray-500" />
                  Edit
                </button>
              )}
              <button
                type="button"
                onClick={() => closeAnd(() => onDuplicate(campaign))}
                className="flex w-full items-center gap-3 rounded-[10px] px-4 py-3 text-left text-[14px] font-medium text-gray-800 active:bg-gray-50 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Copy className="h-4 w-4 text-gray-500" />
                Duplicate
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm('Are you sure you want to delete this campaign?')) {
                    closeAnd(() => onDelete(campaign.id));
                  }
                }}
                className="flex w-full items-center gap-3 rounded-[10px] px-4 py-3 text-left text-[14px] font-medium text-red-600 active:bg-red-50 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
